import { Grid } from "@material-ui/core";
import { noop } from "lodash";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { Button } from "../../../components/button";
import { TextField } from "../../../components/forms/text";
import { useCopying } from "../../../components/hooks/use-copying";
import { Icon } from "../../../components/icons";
import { Flex } from "../../../components/layout";
import { useAppLoader } from "../../../components/loader/app-loader-hook";
import { Widget } from "../../../components/widget";
import { AppStore } from "../../../store";
import { AuthDTO } from "../../../store/auth/models/dto";
import { BillingDTO } from "../../../store/billing/models/dto";
import { BillingService } from "../../../store/billing/service";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../../store/common/fetchable/helpers";
import { blockchainExplorers } from "../../../store/wallets/crypto";
import { toDecimal } from "../../../utils/utils";
import { Status } from "./common";

const StyledWidget = styled(Widget)`
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const Breakable = styled.span`
  word-break: break-all;
`;

export const SingleTransaction: FunctionComponent<{
  item: BillingDTO.Responses.GetSingleTransaction.T;
}> = ({ item: { id } }) => {
  const auth = useSelector<AppStore, Fetchable<AuthDTO.Responses.Login.T>>(
    (store) => store.auth.login
  );
  const item = useSelector<
    AppStore,
    BillingDTO.Responses.GetSingleTransaction.T
  >(
    (store) =>
      store.billing.singleTransactionMap[
        id
      ] as BillingDTO.Responses.GetSingleTransaction.T
  );
  const loader = useAppLoader();

  const {
    buyerWallet,
    credits,
    purchaseTime,
    sekuritanceWallet,
    state,
    transactionHash,
    equivalentIn,
  } = item;

  const { t } = useTranslation("common");

  const [_, setCopying] = useCopying(transactionHash);
  const [amount, setAmount] = useState<number>(equivalentIn.amount);
  const [textValue, setTextValue] = useState<string>(
    toDecimal(equivalentIn.amount)
  );
  const onConfirm = useCallback(
    () =>
      loader(BillingService.confirmTransaction(item.id, amount).catch(noop)),
    [item, amount]
  );

  const exploreAddress = useMemo(() => {
    const func = blockchainExplorers[buyerWallet.chain];
    return func
      ? (id: string) => (
          <a target="_blank" rel="noreferrer" href={func.address(id)}>
            <Breakable>{id}</Breakable>
          </a>
        )
      : (id: string) => <Breakable>{id}</Breakable>;
  }, [buyerWallet]);

  const exploreTx = useMemo(() => {
    const func = blockchainExplorers[buyerWallet.chain];
    return func
      ? (id: string) => (
          <a target="_blank" rel="noreferrer" href={func.tx(id)}>
            <Breakable>{id}</Breakable>
          </a>
        )
      : (id: string) => <Breakable>{id}</Breakable>;
  }, [buyerWallet]);

  return (
    <>
      <StyledWidget title="General">
        <Grid container spacing={3}>
          <Grid item xs={6}>
            {t("status", { context: "title" })}
          </Grid>
          <Grid item xs={6}>
            <Status status={state}>{state}</Status>
          </Grid>
          <Grid item xs={6}>
            {t("credit", { context: "title", count: 2 })}
          </Grid>
          <Grid item xs={6}>
            {toDecimal(credits)}
          </Grid>
          <Grid item xs={6}>
            {t("equivalent-in")}
          </Grid>
          <Grid item xs={6}>
            {equivalentIn.currency} {toDecimal(equivalentIn.amount)}
          </Grid>
          <Grid item xs={6}>
            {t("date", { context: "title" })}
          </Grid>
          <Grid item xs={6}>
            {purchaseTime.local().format("yyyy-MM-DD HH:mm")}
          </Grid>
          {state === "PENDING" &&
          isFetchableSuccess(auth) &&
          auth.data.accessToken.roles.includes("ROLE_ADMIN") ? (
            <>
              <Grid item xs={6}>
                {t("enter-the-value-shown-on-chain", { ns: "billing" })}
              </Grid>
              <Grid item xs={1}>
                <TextField
                  value={textValue}
                  fullWidth
                  onChange={(e: any) => {
                    setTextValue(e.target.value);
                    const parsed = parseFloat(e.target.value);
                    if (!isNaN(parsed)) {
                      setAmount(parsed);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <Flex>
                  <Button onClick={onConfirm}>
                    {t("confirm", { context: "title" })} USDT{" "}
                    {toDecimal(amount)}
                  </Button>
                </Flex>
              </Grid>
            </>
          ) : null}
        </Grid>
      </StyledWidget>
      <StyledWidget title="Chain Details">
        <Grid container spacing={3}>
          <Grid item xs={6}>
            {t("transaction-#")}
          </Grid>
          <Grid item xs={6}>
            {exploreTx(transactionHash)}{" "}
            <Icon type="copy" onClick={setCopying} inline />
          </Grid>
          <Grid item xs={6}>
            {t("source-wallet-address", { ns: "billing" })}
          </Grid>
          <Grid item xs={6}>
            {exploreAddress(buyerWallet.address)}
          </Grid>
          <Grid item xs={6}>
            {t("source-wallet-chain", { ns: "billing" })}
          </Grid>
          <Grid item xs={6}>
            {buyerWallet.chain}
          </Grid>
          {sekuritanceWallet ? (
            <>
              <Grid item xs={6}>
                {t("destination-wallet-address", { ns: "fields" })}
              </Grid>
              <Grid item xs={6}>
                {exploreAddress(sekuritanceWallet.address)}
              </Grid>
              <Grid item xs={6}>
                {t("destination-wallet-chain", { ns: "fields" })}
              </Grid>
              <Grid item xs={6}>
                {sekuritanceWallet.chain}
              </Grid>
            </>
          ) : null}
        </Grid>
      </StyledWidget>
    </>
  );
};
